import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3009/api/v1';

export interface RbacRoleSummary {
  id: number;
  name: string;
  description: string | null;
  is_system: boolean;
}

export interface RbacModuleAction {
  actionKey: string;
  label: string;
  enabled: boolean;
}

export interface RbacModulePermission {
  moduleId: number;
  moduleKey: string;
  label: string;
  icon: string | null;
  sortOrder: number;
  canAccess: boolean;
  actions: RbacModuleAction[];
  recordingMaxHoursBack: number | null;
}

export interface RbacRoleDetail {
  role: RbacRoleSummary;
  modules: RbacModulePermission[];
}

export interface RbacUserRow {
  id: number;
  email: string;
  username: string | null;
  active: boolean;
  roleId: number | null;
  roleName: string | null;
  createdAt: string;
}

export interface RbacUserDetail {
  user: RbacUserRow;
  effectiveRole: RbacRoleDetail | null;
}

function pick<T>(obj: Record<string, unknown>, camel: string, snake: string): T | undefined {
  const v = (obj as any)[camel];
  if (v !== undefined && v !== null) return v as T;
  return (obj as any)[snake] as T | undefined;
}

function normalizeRoleSummary(raw: Record<string, unknown>): RbacRoleSummary {
  const id = Number(raw.id);
  const name = String(raw.name ?? '');
  const desc = raw.description;
  const isSys = raw.is_system ?? raw.isSystem;
  return {
    id: Number.isFinite(id) ? id : 0,
    name,
    description: desc === undefined || desc === null ? null : String(desc),
    is_system: Boolean(isSys)
  };
}

function normalizeModulePermission(m: Record<string, unknown>): RbacModulePermission {
  const moduleId = Number(pick(m, 'moduleId', 'module_id') ?? 0);
  const moduleKey = String(pick(m, 'moduleKey', 'module_key') ?? '');
  const label = String(pick(m, 'label', 'label') ?? '');
  const icon = (pick<string | null>(m, 'icon', 'icon') ?? null) as string | null;
  const sortOrder = Number(pick(m, 'sortOrder', 'sort_order') ?? 0);
  const canAccess = Boolean(pick(m, 'canAccess', 'can_access'));
  const rawActions = (pick<unknown[]>(m, 'actions', 'actions') ?? []) as Record<string, unknown>[];
  const actions = rawActions.map((a) => ({
    actionKey: String(pick(a, 'actionKey', 'action_key') ?? ''),
    label: String(pick(a, 'label', 'label') ?? ''),
    enabled: Boolean(pick(a, 'enabled', 'enabled'))
  }));
  const rawMaxHours = pick<number | null>(m, 'recordingMaxHoursBack', 'recording_max_hours_back');
  const recordingMaxHoursBack = rawMaxHours != null && Number.isFinite(Number(rawMaxHours)) ? Number(rawMaxHours) : null;
  return {
    moduleId: Number.isFinite(moduleId) ? moduleId : 0,
    moduleKey,
    label,
    icon,
    sortOrder: Number.isFinite(sortOrder) ? sortOrder : 0,
    canAccess,
    actions,
    recordingMaxHoursBack
  };
}

function normalizeRbacUserRow(r: Record<string, unknown>): RbacUserRow {
  return {
    id: Number(r.id),
    email: String(r.email ?? ''),
    username: (r.username as string | null) ?? null,
    active: Boolean(r.active),
    roleId: r.roleId != null ? Number(r.roleId) : r.role_id != null ? Number(r.role_id) : null,
    roleName: (r.roleName as string | null) ?? (r.role_name as string | null) ?? null,
    createdAt: String(r.createdAt ?? r.created_at ?? '')
  };
}

function normalizeRoleDetail(raw: Record<string, unknown>): RbacRoleDetail {
  const roleRaw = (pick<Record<string, unknown>>(raw, 'role', 'role') ?? {}) as Record<string, unknown>;
  const modulesRaw = (pick<unknown[]>(raw, 'modules', 'modules') ?? []) as Record<string, unknown>[];
  return {
    role: normalizeRoleSummary(roleRaw),
    modules: modulesRaw.map((m) => normalizeModulePermission(m))
  };
}

function normalizeRbacUserDetail(raw: Record<string, unknown>): RbacUserDetail {
  const userRaw = (pick<Record<string, unknown>>(raw, 'user', 'user') ?? {}) as Record<string, unknown>;
  const erRaw = pick<Record<string, unknown> | null>(raw, 'effectiveRole', 'effective_role');
  const effectiveRole =
    erRaw && typeof erRaw === 'object' && Object.keys(erRaw).length > 0
      ? normalizeRoleDetail(erRaw as Record<string, unknown>)
      : null;
  return {
    user: normalizeRbacUserRow(userRaw),
    effectiveRole
  };
}

function unwrapData<T>(body: unknown): T | undefined {
  if (body == null || typeof body !== 'object') return undefined;
  const b = body as Record<string, unknown>;
  if ('data' in b && b.data !== undefined) return b.data as T;
  return body as T;
}

export interface EffectiveRbacMe {
  moduleKeys: string[];
  actionGrants: string[];
  recordingMaxHoursBack: number | null;
}

async function fetchMyEffectiveRbac(): Promise<EffectiveRbacMe> {
  const { data } = await axios.get(`${API_URL}/rbac/me/module-keys`);
  const inner = unwrapData<{ moduleKeys?: string[]; actionGrants?: string[]; recordingMaxHoursBack?: number | null }>(data);
  const moduleKeys = inner?.moduleKeys;
  const actionGrants = inner?.actionGrants;
  const rawMaxHours = inner?.recordingMaxHoursBack;
  return {
    moduleKeys: Array.isArray(moduleKeys) ? moduleKeys : [],
    actionGrants: Array.isArray(actionGrants) ? actionGrants : [],
    recordingMaxHoursBack: rawMaxHours != null && Number.isFinite(Number(rawMaxHours)) ? Number(rawMaxHours) : null
  };
}

export const rbacApi = {
  getMyEffectiveRbac: fetchMyEffectiveRbac,

  getMyModuleKeys: async (): Promise<string[]> => {
    const { moduleKeys } = await fetchMyEffectiveRbac();
    return moduleKeys;
  },

  getRoles: async (): Promise<RbacRoleSummary[]> => {
    const { data } = await axios.get(`${API_URL}/rbac/roles`);
    const payload = unwrapData<unknown[]>(data);
    const list = Array.isArray(payload) ? payload : [];
    return list.map((row) => normalizeRoleSummary(row as Record<string, unknown>));
  },

  getRoleDetail: async (roleId: number): Promise<RbacRoleDetail> => {
    const { data } = await axios.get(`${API_URL}/rbac/roles/${roleId}`);
    const payload = unwrapData<Record<string, unknown>>(data);
    if (!payload || typeof payload !== 'object') {
      throw new Error('Respuesta de rol inválida');
    }
    return normalizeRoleDetail(payload);
  },

  updateRolePermissions: async (
    roleId: number,
    modules: Array<{
      moduleKey: string;
      canAccess: boolean;
      actions?: Record<string, boolean>;
      recordingMaxHoursBack?: number | null;
    }>
  ): Promise<RbacRoleDetail> => {
    const { data } = await axios.put(`${API_URL}/rbac/roles/${roleId}/permissions`, {
      modules
    });
    const payload = unwrapData<Record<string, unknown>>(data);
    if (!payload || typeof payload !== 'object') {
      throw new Error('Respuesta de permisos inválida');
    }
    return normalizeRoleDetail(payload);
  },

  getUsers: async (): Promise<RbacUserRow[]> => {
    const { data } = await axios.get(`${API_URL}/rbac/users`);
    const payload = unwrapData<unknown[]>(data);
    const list = Array.isArray(payload) ? payload : [];
    return list.map((row) => normalizeRbacUserRow(row as Record<string, unknown>));
  },

  getUser: async (userId: number): Promise<RbacUserDetail> => {
    const { data } = await axios.get(`${API_URL}/rbac/users/${userId}`);
    const payload = unwrapData<Record<string, unknown>>(data);
    if (!payload || typeof payload !== 'object') {
      throw new Error('Respuesta de usuario inválida');
    }
    return normalizeRbacUserDetail(payload);
  },

  createUser: async (body: {
    email: string;
    password: string;
    username?: string | null;
    roleId?: number | null;
    active?: boolean;
  }): Promise<RbacUserDetail> => {
    const { data } = await axios.post(`${API_URL}/rbac/users`, body);
    const payload = unwrapData<Record<string, unknown>>(data);
    if (!payload || typeof payload !== 'object') {
      throw new Error('Respuesta de usuario inválida');
    }
    return normalizeRbacUserDetail(payload);
  },

  updateUser: async (
    userId: number,
    body: {
      email?: string;
      username?: string | null;
      active?: boolean;
      roleId?: number | null;
      password?: string;
      adminCurrentPassword?: string;
    }
  ): Promise<RbacUserDetail> => {
    const { data } = await axios.patch(`${API_URL}/rbac/users/${userId}`, body);
    const payload = unwrapData<Record<string, unknown>>(data);
    if (!payload || typeof payload !== 'object') {
      throw new Error('Respuesta de usuario inválida');
    }
    return normalizeRbacUserDetail(payload);
  },

  deleteUser: async (userId: number): Promise<void> => {
    await axios.delete(`${API_URL}/rbac/users/${userId}`);
  },

  getRoleOptions: async (): Promise<Array<{ id: number; name: string }>> => {
    const { data } = await axios.get(`${API_URL}/rbac/roles-options`);
    const payload = unwrapData<unknown[]>(data);
    const list = Array.isArray(payload) ? payload : [];
    return list.map((row) => {
      const r = row as Record<string, unknown>;
      return { id: Number(r.id), name: String(r.name ?? '') };
    });
  },

  updateUserRole: async (userId: number, roleId: number | null): Promise<RbacUserRow | undefined> => {
    const { data } = await axios.patch(`${API_URL}/rbac/users/${userId}/role`, { roleId });
    const payload = unwrapData<Record<string, unknown>>(data);
    if (!payload) return undefined;
    return normalizeRbacUserRow(payload);
  }
};
